import React, {
  ForwardedRef,
  RefObject,
  forwardRef,
  memo,
  useMemo,
  useRef,
} from 'react'
import useBuffering from '$hooks/video/use-buffering'
import { useLastCallback } from '$hooks/use-last-callback'
import { useSyncEffect } from '$hooks/use-sync-effect'
import useVideoCleanup from '$hooks/video/use-video-cleanup'
import useVideoAutoPause from '$hooks/video/use-video-auto-pause'
import { AnyFunction, NoneToVoidFunction } from '$hooks/types'

type VideoProps = React.DetailedHTMLProps<
  React.VideoHTMLAttributes<HTMLVideoElement>,
  HTMLVideoElement
>

type OwnProps = {
  canPlay: boolean
  children?: React.ReactNode
  onReady?: NoneToVoidFunction
  onBroken?: NoneToVoidFunction
} & VideoProps

const Video = forwardRef(function OptimizedVideoComponent(
  {
    canPlay,
    children,
    onReady,
    onBroken,
    onTimeUpdate,
    ...restProps
  }: OwnProps,
  ref: ForwardedRef<HTMLVideoElement>,
) {
  const localRef = useRef<HTMLVideoElement>(null)
  if (!ref) {
    ref = localRef
  }

  const { handlePlaying: handlePlayingForAutoPause } = useVideoAutoPause(
    ref as RefObject<HTMLVideoElement>,
    canPlay,
  )
  useVideoCleanup(ref as RefObject<HTMLVideoElement>, [])

  const isReadyRef = useRef(false)
  const handleReady = useLastCallback(() => {
    if (!isReadyRef.current) {
      onReady?.()
      isReadyRef.current = true
    }
  })

  // This is only needed for browsers not allowing autoplay
  const { isBuffered, bufferingHandlers } = useBuffering(
    true,
    onTimeUpdate,
    onBroken,
  )
  const {
    onPlaying: handlePlayingForBuffering,
    ...otherBufferingHandlers
  } = bufferingHandlers
  useSyncEffect(
    ([prevIsBuffered]) => {
      if (prevIsBuffered === undefined) {
        return
      }

      handleReady()
    },
    [isBuffered, handleReady],
  )

  const handlePlaying = useLastCallback(e => {
    handlePlayingForAutoPause()
    handlePlayingForBuffering(e)
    handleReady()
    restProps.onPlaying?.(e)
  })

  const mergedOtherBufferingHandlers = useMemo(() => {
    const mergedHandlers: Record<string, AnyFunction> = {}
    Object.keys(otherBufferingHandlers).forEach(keyString => {
      const key = keyString as keyof typeof otherBufferingHandlers
      mergedHandlers[key] = (event: Event) => {
        restProps[key as keyof typeof restProps]?.(event)
        otherBufferingHandlers[key]?.(event)
      }
    })

    return mergedHandlers
  }, [otherBufferingHandlers, restProps])

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <video
      ref={ref}
      autoPlay
      {...restProps}
      {...mergedOtherBufferingHandlers}
      onPlaying={handlePlaying}
    >
      {children}
    </video>
  )
})

export const OptimizedVideo = memo(Video)
